import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../../state/configureStore';
import { fetchSubtaskTypeList, SubtaskTypeState } from '../../../../state/subtaskType';
import { clearTaskCreateDialog } from '../../../../state/taskCreate';
import ConfirmDialog from '../../../components/ConfirmDialog';
import SubtaskListDetailDialog from './SubtaskListDetailDialog';
import SubtaskTypeSelectDialog from './SubtaskTypeSelectDialog';
import TaskFormDialog from './TaskFormDialog';
import TaskItemForecastListDetailDialog from './TaskItemForecastListDetailDialog';
import TaskItemListDialog from './TaskItemListDialog';
import TaskItemMoqListDetailDialog from './TaskItemMoqListDetailDialog';
import { TransitionProps } from '@material-ui/core/transitions';

type dialogStep =
  | 'Create Task'
  | 'Select Subtask Type'
  | 'Create Subtask'
  | 'Select Item'
  | 'Set MOQ'
  | 'Set Forecast'
  | 'Finish';

interface TaskCreateDialogProps {
  open: boolean;
  onClose: () => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return (
    <Slide direction="up" ref={ref} {...props} timeout={{ enter: 400, exit: 400 }} />
  );
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogPaper: {
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 30px 80px rgba(15, 23, 42, 0.24)',
    },
    finishTitle: {
      padding: 0,
      background:
        'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 30%), linear-gradient(135deg, #072577 0%, #1d4ed8 55%, #60a5fa 100%)',
    },
    finishTitleContent: {
      padding: theme.spacing(2, 3, 1),
    },
    finishKicker: {
      color: 'rgba(191, 219, 254, 0.88)',
      fontSize: '0.72rem',
      fontWeight: 800,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      marginBottom: theme.spacing(0.75),
    },
    finishHeading: {
      color: '#f8fafc',
      fontWeight: 800,
      letterSpacing: '-0.03em',
    },
    finishSubtitle: {
      color: 'rgba(226, 232, 240, 0.84)',
      lineHeight: 1.7,
      maxWidth: 560,
    },
    finishContent: {
      padding: theme.spacing(3, 3, 3.5),
      background:
        'linear-gradient(180deg, rgba(248,250,252,0.94) 0%, rgba(255,255,255,1) 100%)',
    },
    finishSurface: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      padding: theme.spacing(2.25),
      borderRadius: 22,
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
      border: '1px solid rgba(148, 163, 184, 0.18)',
      boxShadow: '0 24px 50px rgba(15, 23, 42, 0.08)',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    progressShell: {
      width: 72,
      height: 72,
      borderRadius: 20,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background:
        'linear-gradient(135deg, rgba(219,234,254,0.9) 0%, rgba(239,246,255,0.96) 100%)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
      flexShrink: 0,
    },
    progressIndicator: {
      color: '#2563eb',
    },
    finishBody: {
      minWidth: 0,
    },
    finishBodyTitle: {
      color: '#0f172a',
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    finishBodyText: {
      marginTop: theme.spacing(0.5),
      color: '#64748b',
      lineHeight: 1.65,
      maxWidth: 520,
    },
    finishNote: {
      marginTop: theme.spacing(1.25),
      display: 'inline-flex',
      alignItems: 'center',
      padding: theme.spacing(0.7, 1.2),
      borderRadius: 999,
      fontSize: '0.82rem',
      fontWeight: 700,
      color: '#1d4ed8',
      background: 'rgba(219, 234, 254, 0.72)',
      border: '1px solid rgba(96, 165, 250, 0.24)',
    },
  }),
);

const TaskCreateDialog: React.FC<TaskCreateDialogProps> = (props) => {
  const classes = useStyles();
  const { open, onClose } = props;
  const dispatch = useDispatch();
  const [step, setStep] = useState<dialogStep>('Create Task');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const { items: subtaskTypeList } = useSelector<AppState, SubtaskTypeState>(
    (state) => state.subtaskType,
  );

  const taskFormNextClick = () => {
    setStep('Select Subtask Type');
  };
  const subtaskTypeSelectNextClick = () => {
    setStep('Create Subtask');
  };
  const subtaskTypeSelectBackClick = () => {
    setStep('Create Task');
  };
  const subtaskListDetailNextClick = () => {
    setStep('Select Item');
  };
  const subtaskListDetailBackClick = () => {
    setStep('Select Subtask Type');
  };
  const taskItemListBackClick = () => {
    setStep('Create Subtask');
  };
  const taskItemListNextClick = () => {
    setStep('Set MOQ');
  };
  const taskItemMoqListBackClick = () => {
    setStep('Select Item');
  };
  const taskItemMoqListNextClick = () => {
    setStep('Set Forecast');
  };
  const taskItemForecastListBackClick = () => {
    setStep('Set MOQ');
  };
  const taskItemForecastListNextClick = () => {
    setStep('Finish');
  };
  const finishClick = () => {
    setStep('Finish');
  };
  const handleClose = () => {
    setOpenConfirmDialog(true);
  };

  useEffect(() => {
    dispatch(
      fetchSubtaskTypeList(
        { 'IsSpecialType.EqualsTo': 'false' },
        { pageNumber: 0, pageSize: 15 },
      ),
    );
  }, [dispatch]);
  return (
    <>
      <ConfirmDialog
        open={openConfirmDialog}
        content={'Are you sure you want to close this dialog?'}
        handleCancel={() => {
          setOpenConfirmDialog(false);
        }}
        handleConfirm={() => {
          setOpenConfirmDialog(false);
          onClose();
          setStep('Create Task');
          dispatch(clearTaskCreateDialog());
        }}
        handleClose={() => {
          setOpenConfirmDialog(false);
        }}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        TransitionComponent={Transition}
        PaperProps={{ className: classes.dialogPaper }}
      >
        {step === 'Create Task' && (
          <TaskFormDialog nextClick={taskFormNextClick} closeClick={handleClose} />
        )}
        {step === 'Select Subtask Type' && (
          <SubtaskTypeSelectDialog
            nextClick={subtaskTypeSelectNextClick}
            backClick={subtaskTypeSelectBackClick}
            subtaskTypeList={subtaskTypeList}
            closeClick={handleClose}
          />
        )}
        {step === 'Create Subtask' && (
          <SubtaskListDetailDialog
            nextClick={subtaskListDetailNextClick}
            backClick={subtaskListDetailBackClick}
            subtaskTypeList={subtaskTypeList}
            closeClick={handleClose}
          />
        )}
        {step === 'Select Item' && (
          <TaskItemListDialog
            nextClick={taskItemListNextClick}
            backClick={taskItemListBackClick}
            finishClick={finishClick}
            closeClick={handleClose}
          />
        )}
        {step === 'Set MOQ' && (
          <TaskItemMoqListDetailDialog
            nextClick={taskItemMoqListNextClick}
            backClick={taskItemMoqListBackClick}
            closeClick={handleClose}
          />
        )}
        {step === 'Set Forecast' && (
          <TaskItemForecastListDetailDialog
            nextClick={taskItemForecastListNextClick}
            backClick={taskItemForecastListBackClick}
            closeClick={handleClose}
          />
        )}
        {step === 'Finish' && (
          <>
            <DialogTitle className={classes.finishTitle} disableTypography>
              <Box className={classes.finishTitleContent}>
                <Typography variant="h4" className={classes.finishHeading}>
                  Creating your task workspace
                </Typography>
                <Typography variant="body1" className={classes.finishSubtitle}>
                  We are setting up everything for you. This may take a few moments.
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent className={classes.finishContent}>
              <Box className={classes.finishSurface}>
                <Box className={classes.progressShell}>
                  <CircularProgress
                    size={32}
                    thickness={4.6}
                    className={classes.progressIndicator}
                  />
                </Box>
                <Box className={classes.finishBody}>
                  <Typography variant="h6" className={classes.finishBodyTitle}>
                    Creating...
                  </Typography>
                  <Typography variant="body2" className={classes.finishBodyText}>
                    Please wait a moment while the system prepares the task flow and links
                    the related data for you.
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

export default TaskCreateDialog;
